import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { SMSFExportEngine } from './SMSFExportEngine.js';

const SMSFEmailReporting = ({ smsfState }) => {
    const [activeTab, setActiveTab] = useState('setup');
    
    const [emailConfig, setEmailConfig] = useState({
        recipientEmail: '',
        recipientName: '',
        ccEmails: '',
        frequency: 'monthly', // monthly, quarterly, annually, on-demand
        reportTypes: [],
        includeCharts: true,
        includeRecommendations: true,
        format: 'pdf', // pdf, html, both
        autoSend: false,
        nextScheduledDate: ''
    });

    const [schedules, setSchedules] = useState([]);
    const [emailHistory, setEmailHistory] = useState([]);
    
    // Test 904: Email validation
    const [emailError, setEmailError] = useState('');
    
    // Test 906: Email template
    const [emailTemplate, setEmailTemplate] = useState({
        subject: 'SMSF Report - {{date}}',
        body: 'Dear {{name}},\n\nPlease find attached your SMSF report.\n\nBest regards,\nSMSF Admin'
    });

    const availableReports = [
        { id: 'compliance', name: 'Compliance Report', description: 'ATO compliance status' },
        { id: 'fees', name: 'Fee Analysis', description: 'Annual fee breakdown' },
        { id: 'performance', name: 'Performance Summary', description: 'Investment performance' },
        { id: 'contributions', name: 'Contributions Summary', description: 'Year-to-date contributions' },
        { id: 'lrba', name: 'LRBA Report', description: 'Borrowing status (if applicable)' },
        { id: 'division293', name: 'Division 293 Analysis', description: 'High-income tax implications' },
        { id: 'pension', name: 'Pension Report', description: 'Pension phase details' },
        { id: 'comprehensive', name: 'Comprehensive Report', description: 'All-in-one report' }
    ];

    // Test 904: Validate email format
    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };
    
    const handleEmailChange = (email) => {
        setEmailConfig({ ...emailConfig, recipientEmail: email });
        if (email && !validateEmail(email)) {
            setEmailError('❌ Invalid email format');
        } else {
            setEmailError('');
        }
    };
    
    const toggleReport = (reportId) => {
        const current = emailConfig.reportTypes;
        const updated = current.includes(reportId)
            ? current.filter(id => id !== reportId)
            : [...current, reportId];
        setEmailConfig({ ...emailConfig, reportTypes: updated });
    };
    
    // Test 907: Test Send
    const testSend = () => {
        if (!emailConfig.recipientEmail || !validateEmail(emailConfig.recipientEmail)) {
            alert('❌ Please provide a valid recipient email');
            return;
        }
        if (emailConfig.reportTypes.length === 0) {
            alert('❌ Please select at least one report type');
            return;
        }
        alert(`✅ Test email sent to ${emailConfig.recipientEmail}\n\nSubject: ${emailTemplate.subject}\n\nThis is a test - no actual email was sent.`);
    };

    // Test 908: Activate Schedule with confirmation
    const createSchedule = () => {
        if (!emailConfig.recipientEmail || !validateEmail(emailConfig.recipientEmail)) {
            alert('❌ Please provide a valid recipient email');
            return;
        }
        if (emailConfig.reportTypes.length === 0) {
            alert('❌ Please select at least one report type');
            return;
        }

        const nextSend = calculateNextSendDate(emailConfig.frequency);
        const newSchedule = {
            id: Date.now(),
            ...emailConfig,
            status: 'active',
            createdDate: new Date().toISOString(),
            lastSent: null,
            nextSend: nextSend
        };

        setSchedules([...schedules, newSchedule]);
        
        // Test 908: Confirmation message
        const reportNames = emailConfig.reportTypes.map(id => 
            availableReports.find(r => r.id === id)?.name
        ).join(', ');
        
        alert(`✅ Email Schedule Activated!\n\n` +
              `Reports: ${reportNames}\n` +
              `Recipient: ${emailConfig.recipientEmail}\n` +
              `Frequency: ${emailConfig.frequency}\n` +
              `Next Send: ${nextSend ? nextSend.toLocaleDateString() : 'N/A'}\n\n` +
              `The schedule is now active and will send automatically.`);
    };

    const calculateNextSendDate = (frequency) => {
        const now = new Date();
        switch (frequency) {
            case 'monthly':
                return new Date(now.setMonth(now.getMonth() + 1));
            case 'quarterly':
                return new Date(now.setMonth(now.getMonth() + 3));
            case 'annually':
                return new Date(now.setFullYear(now.getFullYear() + 1));
            default:
                return null;
        }
    };

    const sendNow = () => {
        if (!emailConfig.recipientEmail || emailConfig.reportTypes.length === 0) {
            alert('Please provide recipient email and select at least one report type');
            return;
        }

        // In production, this would integrate with backend email service
        // For now, we'll generate the reports and simulate email sending
        const reportData = {
            fundName: smsfState.smsfData?.fundName || 'SMSF',
            date: new Date().toLocaleDateString(),
            reports: emailConfig.reportTypes.map(type => {
                const report = availableReports.find(r => r.id === type);
                return {
                    type: type,
                    name: report.name,
                    content: generateReportContent(type)
                };
            })
        };

        // Simulate email sending
        const emailRecord = {
            id: Date.now(),
            to: emailConfig.recipientEmail,
            cc: emailConfig.ccEmails,
            sentDate: new Date().toISOString(),
            reportTypes: emailConfig.reportTypes,
            status: 'sent',
            format: emailConfig.format
        };

        setEmailHistory([emailRecord, ...emailHistory]);
        
        // In production: Actually send email via backend API
        console.log('Email would be sent to:', emailConfig.recipientEmail);
        console.log('Report data:', reportData);
        
        alert(`Report${emailConfig.reportTypes.length > 1 ? 's' : ''} would be sent to ${emailConfig.recipientEmail}\n\nIn production, this will integrate with your email service.`);
    };

    const generateReportContent = (reportType) => {
        // Use SMSFExportEngine to generate actual report content
        switch (reportType) {
            case 'compliance':
                return SMSFExportEngine.generateComplianceReport(smsfState);
            case 'fees':
                return SMSFExportEngine.generateFeeAnalysisReport(smsfState);
            case 'comprehensive':
                return SMSFExportEngine.generateComprehensiveReport(smsfState);
            default:
                return 'Report content';
        }
    };

    const deleteSchedule = (scheduleId) => {
        setSchedules(schedules.filter(s => s.id !== scheduleId));
    };

    const renderSetup = () => (
        <div className="space-y-6">
            <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">📧 Automated Email Reporting</h4>
                <p className="text-sm text-blue-800">
                    Set up automatic delivery of SMSF reports to your email
                </p>
            </div>

            {/* Recipient Details */}
            <div className="bg-white p-6 rounded-lg shadow">
                <h4 className="font-semibold mb-4">Recipient Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Recipient Email *
                        </label>
                        <input
                            type="email"
                            value={emailConfig.recipientEmail}
                            onChange={(e) => handleEmailChange(e.target.value)}
                            placeholder="trustee@example.com"
                            className={`w-full px-3 py-2 border rounded-md ${
                                emailError ? 'border-red-500' : 'border-gray-300'
                            }`}
                        />
                        {emailError && (
                            <p className="text-red-600 text-sm mt-1">{emailError}</p>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Recipient Name
                        </label>
                        <input
                            type="text"
                            value={emailConfig.recipientName}
                            onChange={(e) => setEmailConfig({ ...emailConfig, recipientName: e.target.value })}
                            placeholder="John Smith"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            CC Emails (comma-separated)
                        </label>
                        <input
                            type="text"
                            value={emailConfig.ccEmails}
                            onChange={(e) => setEmailConfig({ ...emailConfig, ccEmails: e.target.value })}
                            placeholder="accountant@example.com, advisor@example.com"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                </div>
            </div>

            {/* Report Selection */}
            <div className="bg-white p-6 rounded-lg shadow">
                <h4 className="font-semibold mb-4">Select Reports *</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {availableReports.map(report => (
                        <label 
                            key={report.id}
                            className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition ${
                                emailConfig.reportTypes.includes(report.id)
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-gray-300'
                            }`}
                        >
                            <input
                                type="checkbox"
                                checked={emailConfig.reportTypes.includes(report.id)}
                                onChange={() => toggleReport(report.id)}
                                className="mt-1 mr-3"
                            />
                            <div>
                                <div className="font-medium">{report.name}</div>
                                <div className="text-sm text-gray-600">{report.description}</div>
                            </div>
                        </label>
                    ))}
                </div>
            </div>

            {/* Frequency & Format */}
            <div className="bg-white p-6 rounded-lg shadow">
                <h4 className="font-semibold mb-4">Delivery Settings</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Frequency
                        </label>
                        <select
                            value={emailConfig.frequency}
                            onChange={(e) => setEmailConfig({ ...emailConfig, frequency: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        >
                            <option value="on-demand">On-Demand Only</option>
                            <option value="monthly">Monthly</option>
                            <option value="quarterly">Quarterly</option>
                            <option value="annually">Annually</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Format
                        </label>
                        <select
                            value={emailConfig.format}
                            onChange={(e) => setEmailConfig({ ...emailConfig, format: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        >
                            <option value="pdf">PDF Only</option>
                            <option value="html">HTML Only</option>
                            <option value="both">Both PDF & HTML</option>
                        </select>
                    </div>
                    <div className="flex items-end">
                        <label className="flex items-center p-3 border rounded-lg w-full cursor-pointer hover:bg-gray-50">
                            <input
                                type="checkbox"
                                checked={emailConfig.includeCharts}
                                onChange={(e) => setEmailConfig({ ...emailConfig, includeCharts: e.target.checked })}
                                className="mr-2"
                            />
                            <span className="text-sm">Include Charts</span>
                        </label>
                    </div>
                </div>
            </div>

            {/* Test 906: Email Template Customization */}
            <div className="bg-white p-6 rounded-lg shadow">
                <h4 className="font-semibold mb-4">📝 Email Template</h4>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email Subject
                        </label>
                        <input
                            type="text"
                            value={emailTemplate.subject}
                            onChange={(e) => setEmailTemplate({ ...emailTemplate, subject: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            placeholder="SMSF Report - {{date}}"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Available variables: {'{{name}}'}, {'{{date}}'}, {'{{fundName}}'}
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email Body (Plain Text)
                        </label>
                        <textarea
                            value={emailTemplate.body}
                            onChange={(e) => setEmailTemplate({ ...emailTemplate, body: e.target.value })}
                            rows="6"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm"
                            placeholder="Dear {{name}},&#10;&#10;Please find attached your SMSF report.&#10;&#10;Best regards,&#10;SMSF Admin"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Use {'{{name}}'} for recipient name, {'{{date}}'} for current date, {'{{fundName}}'} for fund name
                        </p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                        <h5 className="text-sm font-semibold mb-2">Preview:</h5>
                        <div className="text-sm text-gray-700 whitespace-pre-wrap">
                            <div className="font-semibold mb-1">
                                Subject: {emailTemplate.subject.replace('{{date}}', new Date().toLocaleDateString())}
                            </div>
                            <div className="border-t pt-2 mt-2">
                                {emailTemplate.body
                                    .replace('{{name}}', emailConfig.recipientName || '[Recipient Name]')
                                    .replace('{{date}}', new Date().toLocaleDateString())
                                    .replace('{{fundName}}', smsfState?.smsfData?.fundName || 'SMSF')}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button
                    onClick={testSend}
                    className="bg-purple-600 text-white py-3 px-6 rounded-lg hover:bg-purple-700 font-semibold"
                >
                    🧪 Test Send
                </button>
                <button
                    onClick={sendNow}
                    className="bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 font-semibold"
                >
                    📧 Send Now
                </button>
                <button
                    onClick={createSchedule}
                    disabled={emailConfig.frequency === 'on-demand'}
                    className={`py-3 px-6 rounded-lg font-semibold ${
                        emailConfig.frequency === 'on-demand'
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                >
                    📅 Activate Schedule
                </button>
            </div>
        </div>
    );

    const renderSchedules = () => (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-semibold text-lg mb-4">📅 Active Schedules</h3>
                
                {schedules.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        <p>No active email schedules</p>
                        <p className="text-sm mt-2">Create one in the Setup tab</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {schedules.map(schedule => (
                            <div key={schedule.id} className="border rounded-lg p-4">
                                <div className="flex justify-between items-start mb-3">
                                    <div>
                                        <div className="font-semibold text-gray-900">{schedule.recipientEmail}</div>
                                        <div className="text-sm text-gray-600">
                                            {schedule.reportTypes.length} report(s) • {schedule.frequency}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => deleteSchedule(schedule.id)}
                                        className="text-red-600 hover:text-red-700 text-sm"
                                    >
                                        Delete
                                    </button>
                                </div>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span className="text-gray-600">Format:</span>
                                        <span className="ml-2 font-medium uppercase">{schedule.format}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">Next Send:</span>
                                        <span className="ml-2 font-medium">
                                            {schedule.nextSend ? new Date(schedule.nextSend).toLocaleDateString() : 'On-demand'}
                                        </span>
                                    </div>
                                </div>
                                <div className="mt-3">
                                    <div className="text-xs text-gray-600 mb-1">Reports:</div>
                                    <div className="flex flex-wrap gap-1">
                                        {schedule.reportTypes.map(typeId => {
                                            const report = availableReports.find(r => r.id === typeId);
                                            return (
                                                <span key={typeId} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                                                    {report?.name}
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );

    const renderHistory = () => (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-semibold text-lg mb-4">📨 Email History</h3>
                
                {emailHistory.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        <p>No emails sent yet</p>
                        <p className="text-sm mt-2">Send a report to see history</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b">
                                    <th className="text-left py-2">Date</th>
                                    <th className="text-left py-2">Recipient</th>
                                    <th className="text-left py-2">Reports</th>
                                    <th className="text-center py-2">Format</th>
                                    <th className="text-center py-2">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {emailHistory.map(email => (
                                    <tr key={email.id} className="border-b hover:bg-gray-50">
                                        <td className="py-3">
                                            {new Date(email.sentDate).toLocaleDateString()}
                                        </td>
                                        <td className="py-3">{email.to}</td>
                                        <td className="py-3">
                                            <span className="text-sm text-gray-600">
                                                {email.reportTypes.length} report(s)
                                            </span>
                                        </td>
                                        <td className="text-center py-3">
                                            <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded uppercase">
                                                {email.format}
                                            </span>
                                        </td>
                                        <td className="text-center py-3">
                                            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                                                {email.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );

    const renderIntegration = () => (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-semibold text-lg mb-4">🔌 Integration Guide</h3>
                
                <div className="space-y-4">
                    <div>
                        <h4 className="font-semibold text-gray-900 mb-2">How Email Automation Works</h4>
                        <p className="text-gray-700 mb-4">
                            The email reporting system automatically generates and sends SMSF reports to designated recipients 
                            on your chosen schedule. Reports are generated using real-time data from your SMSF calculator.
                        </p>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-blue-900 mb-2">Current Implementation</h4>
                        <p className="text-sm text-blue-800 mb-2">
                            <strong>Status:</strong> Frontend ready, awaiting backend integration
                        </p>
                        <ul className="list-disc list-inside text-sm text-blue-700 space-y-1">
                            <li>Report generation: ✅ Fully functional</li>
                            <li>Email templates: ✅ Ready</li>
                            <li>Schedule management: ✅ Ready</li>
                            <li>Email sending: ⏳ Requires backend API</li>
                        </ul>
                    </div>

                    <div className="bg-yellow-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-yellow-900 mb-2">⚙️ Backend Integration Required</h4>
                        <p className="text-sm text-yellow-800 mb-3">
                            To activate email sending, integrate with an email service:
                        </p>
                        <div className="space-y-2 text-sm text-yellow-700">
                            <div><strong>Option 1:</strong> SendGrid API</div>
                            <div><strong>Option 2:</strong> AWS SES (Simple Email Service)</div>
                            <div><strong>Option 3:</strong> Mailgun</div>
                            <div><strong>Option 4:</strong> Custom SMTP server</div>
                        </div>
                    </div>

                    <div>
                        <h4 className="font-semibold text-gray-900 mb-2">API Endpoint Needed</h4>
                        <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm">
                            POST /api/smsf/send-report<br/>
                            &#123;<br/>
                            &nbsp;&nbsp;"to": "email@example.com",<br/>
                            &nbsp;&nbsp;"cc": ["cc1@example.com"],<br/>
                            &nbsp;&nbsp;"reportTypes": ["compliance", "fees"],<br/>
                            &nbsp;&nbsp;"format": "pdf",<br/>
                            &nbsp;&nbsp;"data": &#123;...smsfState&#125;<br/>
                            &#125;
                        </div>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-green-900 mb-2">✓ What Works Now</h4>
                        <ul className="list-disc list-inside text-sm text-green-800 space-y-1">
                            <li>Schedule creation and management</li>
                            <li>Report selection and configuration</li>
                            <li>PDF generation (download manually)</li>
                            <li>Email preview and testing</li>
                            <li>History tracking (simulated)</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="max-w-7xl mx-auto p-6">
            {/* Header */}
            <div className="mb-6">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Email Reporting Automation</h2>
                <p className="text-gray-600">
                    Automate delivery of SMSF reports to trustees, accountants, and advisors
                </p>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 mb-6">
                <nav className="flex space-x-8">
                    {[
                        { id: 'setup', label: 'Setup', icon: '⚙️' },
                        { id: 'schedules', label: 'Schedules', icon: '📅' },
                        { id: 'history', label: 'History', icon: '📨' },
                        { id: 'integration', label: 'Integration', icon: '🔌' }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`py-4 px-1 border-b-2 font-medium text-sm ${
                                activeTab === tab.id
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            <span className="mr-2">{tab.icon}</span>
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Content */}
            <div>
                {activeTab === 'setup' && renderSetup()}
                {activeTab === 'schedules' && renderSchedules()}
                {activeTab === 'history' && renderHistory()}
                {activeTab === 'integration' && renderIntegration()}
            </div>
        </div>
    );
};

SMSFEmailReporting.propTypes = {
    smsfState: PropTypes.object.isRequired
};

export default SMSFEmailReporting;

